import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/colors';
import { globalStyles } from '../styles/globalStyles';
import CustomButton from '../components/CustomButton';
import { useUser } from '../context/UserContext';

const getResponsiveValue = (small, medium, large) => {
  const width = window.innerWidth;
  if (width >= 1024) return large;
  if (width >= 768) return medium;
  return small;
};

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useUser();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await login(formData.email, formData.password);

      if (response.success) {
        const isAdminUser = response.data.is_admin === true ||
                            response.data.role === 'Administrator' ||
                            response.data.role === 'Super Admin' ||
                            response.data.role === 'Placement Officer' ||
                            response.data.role === 'Head Office' ||
                            response.data.role === 'Hall Manager';

        if (!isAdminUser) {
          alert('Access denied. This login is for administrators only.');
          return;
        }

        setTimeout(() => navigate('/admin', { state: { userData: response.data } }), 100);
      } else {
        alert(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        <div style={styles.header}>
          <div style={styles.title}>Admin Portal</div>
          <div style={styles.subtitle}>Restricted access — administrators only</div>
        </div>

        <div style={styles.formContainer}>
          <div style={styles.formCard}>
            <div style={styles.cardTitle}>Admin Sign In</div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                style={styles.input}
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                onKeyPress={handleKeyPress}
                placeholder="Enter admin email"
                autoComplete="email"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  style={styles.passwordInput}
                  value={formData.password}
                  onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  style={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <CustomButton
              title={loading ? 'Signing In...' : 'Sign In'}
              onPress={handleLogin}
              disabled={loading || !formData.email || !formData.password}
              style={styles.loginButton}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  header: {
    backgroundColor: '#1a1a2e',
    padding: getResponsiveValue(24, 32, 40),
    textAlign: 'center',
  },
  title: {
    fontSize: getResponsiveValue(24, 28, 32),
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: getResponsiveValue(8, 10, 12),
  },
  subtitle: {
    fontSize: getResponsiveValue(13, 15, 16),
    color: '#aaa',
  },
  formContainer: {
    padding: getResponsiveValue(20, 32, 40),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '70vh',
  },
  formCard: {
    ...globalStyles.card,
    width: '100%',
    maxWidth: getResponsiveValue(380, 420, 460),
    padding: getResponsiveValue(24, 32, 40),
  },
  cardTitle: {
    fontSize: getResponsiveValue(18, 22, 26),
    fontWeight: 'bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: getResponsiveValue(24, 32, 36),
  },
  inputGroup: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  label: {
    fontSize: getResponsiveValue(13, 14, 16),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
    display: 'block',
  },
  input: {
    width: '100%',
    padding: getResponsiveValue(12, 14, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 10, 12),
    fontSize: getResponsiveValue(14, 15, 16),
    color: colors.text,
    backgroundColor: colors.white,
    outline: 'none',
    boxSizing: 'border-box',
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    width: '100%',
    padding: getResponsiveValue(12, 14, 16),
    paddingRight: 44,
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 10, 12),
    fontSize: getResponsiveValue(14, 15, 16),
    color: colors.text,
    backgroundColor: colors.white,
    outline: 'none',
    boxSizing: 'border-box',
  },
  passwordToggle: {
    position: 'absolute',
    right: 10,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
  },
  loginButton: {
    width: '100%',
    marginTop: getResponsiveValue(8, 12, 16),
  },
};

export default AdminLoginPage;
